/**
 * Closeout · review recap before sending (wireframe 06).
 *
 * Supervisor/admin review the auto-assembled recap (crew, hours, tasks,
 * punch/RMA), edit a short narrative, then close + send — or save a draft.
 * Replaces the old "Preview recap → raw PDF" jump. Route:
 * /projects/:id/closeout.
 *
 * The narrative auto-draft is Pillar D.4; here it's editable text.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Download, ExternalLink, Loader2, Send, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import {
  openRecapPdf,
  useCloseProject,
  useDraftRecapNarrative,
  useProjectRecap,
  useUpdateProject,
} from '@/api/projects';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/Dialog';
import { RecapNotesCard } from '@/components/projects/RecapNotesCard';
import { RecapGalleryCurator } from '@/components/projects/RecapGalleryCurator';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { formatDate, formatMoneyCents } from '@/lib/format';

function fmtMinutes(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h ? (m ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
}

export function ProjectCloseout() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: recap, isLoading, error } = useProjectRecap(id);
  const { data: me } = useCurrentUser();
  const canManage = me?.role === 'supervisor' || me?.role === 'admin';
  const update = useUpdateProject(id ?? '');
  const close = useCloseProject(id ?? '');
  const draft = useDraftRecapNarrative(id ?? '');
  const [narrative, setNarrative] = useState('');
  const [dirty, setDirty] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  // Seed the textarea from the saved narrative once, unless the user is editing.
  useEffect(() => {
    if (recap && !dirty) setNarrative(recap.recap_narrative ?? '');
  }, [recap, dirty]);

  if (isLoading) {
    return (
      <div className="h-full grid place-items-center">
        <Loader2 className="w-5 h-5 animate-spin text-ink-faint" />
      </div>
    );
  }
  if (error || !recap) {
    return (
      <div className="p-6 text-sm text-status-blocked-fg">
        {(error as Error | undefined)?.message ?? 'Recap not found.'}
      </div>
    );
  }

  const isComplete = recap.status === 'complete';
  const openPunch = recap.punch_items_total - recap.punch_items_closed;

  async function downloadRecapPdf() {
    try {
      await openRecapPdf(recap!.project_id);
    } catch (e) {
      toast.error(`Could not open recap PDF: ${(e as Error).message}`);
    }
  }

  function saveDraft() {
    update.mutate(
      { recap_narrative: narrative.trim() || null },
      {
        onSuccess: () => {
          setDirty(false);
          toast.success('Draft saved');
        },
        onError: (e) => toast.error(`Save failed: ${e.message}`),
      },
    );
  }

  function closeAndSend() {
    setConfirmClose(false);
    close.mutate(
      { recap_narrative: narrative.trim() || null },
      {
        onSuccess: () => {
          setDirty(false);
          toast.success('Recap sent · project complete');
        },
        onError: (e) => toast.error(`Send failed: ${e.message}`),
      },
    );
  }

  return (
    <div className="p-6 max-w-5xl space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button
            onClick={() => navigate(`/projects/${id}`)}
            className="flex items-center gap-1.5 text-sm text-ink-dim hover:text-ink mb-1"
          >
            <ArrowLeft className="w-4 h-4" /> Project
          </button>
          <h1 className="text-2xl font-semibold">Review recap before sending</h1>
          <p className="text-ink-dim text-sm">
            {recap.title} · {recap.client_name}
            {recap.sap_order_number ? ` · Order ${recap.sap_order_number}` : ''}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={downloadRecapPdf}>
            <Download className="w-3.5 h-3.5" /> Download PDF
          </Button>
          <Button variant="outline" size="sm" onClick={saveDraft} disabled={update.isPending || !dirty}>
            {update.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save draft
          </Button>
          {!isComplete && (
            <Button size="sm" onClick={() => setConfirmClose(true)} disabled={close.isPending}>
              {close.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5" />
              )}
              Close + send recap
            </Button>
          )}
        </div>
      </div>

      {/* The highest-stakes action in the app gets a real dialog, not a
          native confirm() (audit UX pass). */}
      <ConfirmDialog
        open={confirmClose}
        onClose={() => setConfirmClose(false)}
        onConfirm={closeAndSend}
        title="Close + send recap?"
        body={
          openPunch > 0
            ? `${openPunch} punch item${openPunch === 1 ? '' : 's'} still open — ${openPunch === 1 ? 'it' : 'they'}'ll remain as a schedulable follow-up and ${openPunch === 1 ? 'is' : 'are'} listed in the recap. The project is marked complete and the recap goes out to stakeholders now.`
            : 'The project is marked complete and the recap goes out to stakeholders now.'
        }
        confirmLabel="Close + send"
        busy={close.isPending}
      />

      {isComplete && (
        <div className="rounded-md border border-status-complete-fg/30 bg-status-complete text-status-complete-fg px-4 py-2 text-sm flex items-center gap-2 flex-wrap">
          <CheckCircle2 className="w-4 h-4" /> Recap sent.
          {recap.completed_at && ` Closed ${formatDate(recap.completed_at)}.`}
          {recap.status_page_slug && (
            <a
              href={`/recap/${recap.status_page_slug}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-medium underline ml-1"
            >
              View the recap page recipients got <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-5">
        {/* Auto-assembled preview */}
        <div className="md:col-span-2 space-y-4">
          <div className="bg-bg rounded-xl border border-line p-4 grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
            <Metric n={String(recap.tasks.length)} label="Tasks" />
            <Metric n={fmtMinutes(recap.total_duration_minutes)} label="Duration" />
            {recap.committed_man_minutes != null ? (
              <Metric
                n={fmtMinutes(recap.committed_man_minutes)}
                label="Committed man-hrs"
                sub={
                  recap.overage_man_minutes > 0
                    ? `+${fmtMinutes(recap.overage_man_minutes)} over`
                    : undefined
                }
              />
            ) : (
              <Metric n={fmtMinutes(recap.total_crewed_minutes)} label="Man-hours" />
            )}
            <Metric n={`${recap.punch_items_closed}/${recap.punch_items_total}`} label="Punch closed" />
            <Metric n={formatMoneyCents(recap.actual_cost_cents)} label="Actual cost" />
          </div>

          {recap.committed_man_minutes != null && (
            <p className="text-xs text-ink-faint -mt-2 px-1">
              Committed {fmtMinutes(recap.committed_man_minutes)} man-hours
              {recap.overage_man_minutes > 0 ? (
                <span className="text-status-blocked-fg">
                  {' '}
                  · ran {fmtMinutes(recap.overage_man_minutes)} over (
                  {fmtMinutes(recap.total_crewed_minutes)} actual) — called out to the requester
                </span>
              ) : (
                <span> · {fmtMinutes(recap.total_crewed_minutes)} actual, within the committed floor</span>
              )}
            </p>
          )}

          {recap.crew_roster.length > 0 && (
            <Card title="Crew on this project">
              <div className="flex flex-wrap gap-4">
                {recap.crew_roster.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Avatar name={c.display_name} />
                    <div>
                      <div className="font-medium">{c.display_name}</div>
                      <div className="text-xs text-ink-faint">
                        {c.crew_role}
                        {c.is_lead && c.crew_role !== 'lead' && ' · lead'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card title="What was done">
            <ul className="space-y-2">
              {recap.tasks.map((t) => (
                <li
                  key={t.id}
                  className="flex items-center justify-between gap-3 text-sm border-b border-line last:border-0 pb-2 last:pb-0"
                >
                  <div className="min-w-0">
                    <span className="font-medium">{t.title}</span>
                    {t.sap_rma_reference && (
                      <span className="ml-2 text-xs font-mono text-ink-faint">
                        RMA {t.sap_rma_reference}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-ink-faint whitespace-nowrap">
                    <span className="capitalize">{t.status.replace(/_/g, ' ')}</span>
                    {t.actual_duration_minutes != null && (
                      <span>
                        {fmtMinutes(t.actual_duration_minutes)} ·{' '}
                        {fmtMinutes(t.actual_duration_minutes * Math.max(1, t.crew?.length ?? 1))} man-hrs
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Card>

          {recap.punch_items_total > 0 && (
            <Card title={`Punch list · ${recap.punch_items_closed}/${recap.punch_items_total} closed`}>
              {openPunch > 0 ? (
                <div className="text-sm text-status-blocked-fg">
                  {openPunch} item{openPunch === 1 ? '' : 's'} still open. Closing still marks the
                  project complete — the open {openPunch === 1 ? 'item stays' : 'items stay'} as a
                  follow-up you can schedule, and {openPunch === 1 ? "it's" : "they're"} listed in the
                  recap.
                </div>
              ) : (
                <div className="text-sm text-status-complete-fg">All punch items closed.</div>
              )}
            </Card>
          )}

          {recap.rma_count > 0 && (
            <Card title="RMA summary">
              <div className="text-sm text-ink-dim">
                {recap.rma_count} RMA{recap.rma_count === 1 ? '' : 's'} recorded on this project.
              </div>
            </Card>
          )}

          {/* Recap photos — the supervisor curates the client-facing gallery
              from the crew's captured photos (opt-in + ordered). */}
          {canManage && (
            <RecapGalleryCurator
              projectId={recap.project_id}
              notes={recap.recap_notes ?? []}
              selectedGallery={recap.recap_gallery ?? []}
            />
          )}
          {/* Crew notes — read-only source material that informs the summary +
              the photo picks; never shown verbatim to the requester. */}
          <RecapNotesCard projectId={recap.project_id} variant="curate" />
        </div>

        {/* Narrative + recipients */}
        <div className="space-y-4">
          <Card title="Summary / message">
            <textarea
              value={narrative}
              onChange={(e) => {
                setNarrative(e.target.value);
                setDirty(true);
              }}
              placeholder="Short summary for the recap + the 'recap available' message…"
              rows={8}
              className="w-full rounded-lg border border-line bg-fill-soft p-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <div className="flex items-center justify-between gap-2 mt-2">
              <p className="text-xs text-ink-faint">
                Appears in the recap PDF and the notification.
              </p>
              {/* D.4: drafts INTO the textarea — the supervisor reads, edits,
                  and decides; the AI never writes the recap unsupervised. */}
              <Button
                variant="outline"
                size="sm"
                disabled={draft.isPending}
                onClick={() =>
                  draft.mutate(undefined, {
                    onSuccess: (r) => {
                      setNarrative(r.narrative);
                      setDirty(true);
                      toast.success(
                        r.source === 'stub'
                          ? 'Draft assembled from the recap facts (no AI key — stub mode)'
                          : 'Draft written from the recap — read it over before sending',
                      );
                    },
                    onError: (e) => toast.error(`Draft failed: ${e.message}`),
                  })
                }
              >
                {draft.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5" />
                )}
                Draft with AI
              </Button>
            </div>
          </Card>

          <Card title="Recipients">
            <div className="text-sm text-ink-dim space-y-1">
              <div>PM · {recap.pm_display_name ?? '—'}</div>
              {recap.sales_executive && <div>Sales · {recap.sales_executive}</div>}
              {recap.site_contact_name && <div>Site contact · {recap.site_contact_name}</div>}
              <p className="text-xs text-ink-faint pt-1">
                Each gets an email with the overview above, the recap PDF attached, and a link to
                the full web recap (your summary + the photos you picked). Delivered per the
                notification matrix when you send.
              </p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Metric({ n, label, sub }: { n: string; label: string; sub?: string }) {
  return (
    <div>
      <div className="text-xl font-semibold tabular-nums">{n}</div>
      <div className="text-[11px] uppercase tracking-wide text-ink-faint">{label}</div>
      {sub && <div className="text-[11px] text-status-blocked-fg tabular-nums">{sub}</div>}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-bg rounded-xl border border-line p-4">
      <div className="text-[10px] uppercase tracking-widest text-ink-faint mb-3">{title}</div>
      {children}
    </div>
  );
}

